// Theme control: cycles System -> Light -> Dark.
//
// System removes `data-theme` so the prefers-color-scheme blocks stay in
// charge and the page keeps tracking the OS.
const STORAGE_KEY = "theme";
const MODES = ["system", "light", "dark"];

const LABELS = {
  system: "follows your device",
  light: "light",
  dark: "dark",
};

const ICONS = {
  system: "#i-monitor",
  light: "#i-sun",
  dark: "#i-moon",
};

const storedMode = () => {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === "light" || value === "dark" ? value : "system";
  } catch {
    return "system"; // Private browsing or blocked storage.
  }
};

// Reads the attribute first, not storage: when localStorage throws, the
// attribute is the only thing holding the cycle position.
const currentMode = () => {
  const mode = document.documentElement.dataset.themeMode;
  return MODES.includes(mode) ? mode : storedMode();
};

const nextMode = (mode) => MODES[(MODES.indexOf(mode) + 1) % MODES.length];

export default function initTheme() {
  const toggle = document.querySelector(".theme-toggle");
  if (!toggle) return;

  const icon = toggle.querySelector("use");

  const label = (mode) => {
    toggle.setAttribute(
      "aria-label",
      `Theme: ${LABELS[mode]}. Switch to ${LABELS[nextMode(mode)]} theme.`,
    );
    icon?.setAttribute("href", ICONS[mode]);
  };

  const apply = (mode) => {
    const root = document.documentElement;
    root.dataset.themeMode = mode;

    if (mode === "system") {
      root.removeAttribute("data-theme");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to clear.
      }
    } else {
      root.setAttribute("data-theme", mode);
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // Choice applies to this page view only.
      }
    }

    label(mode);
  };

  apply(currentMode());

  toggle.addEventListener("click", (event) => {
    const next = nextMode(currentMode());

    const canTransition =
      typeof document.startViewTransition === "function" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (!canTransition) {
      apply(next);
      return;
    }

    // Circular wipe out of the button: the outgoing snapshot is held still
    // and the new one is clipped open over it (see base/_graphics.scss).
    const rect = toggle.getBoundingClientRect();
    const x = event.clientX || rect.left + rect.width / 2;
    const y = event.clientY || rect.top + rect.height / 2;
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    const root = document.documentElement;
    root.style.setProperty("--wipe-x", `${x}px`);
    root.style.setProperty("--wipe-y", `${y}px`);
    root.style.setProperty("--wipe-r", `${radius}px`);
    root.dataset.wipe = "";

    // The switch must not depend on the transition resolving: if one is
    // already running it may never invoke its callback. `apply` is idempotent,
    // so whichever path runs first wins.
    const settle = window.setTimeout(() => {
      if (currentMode() !== next) apply(next);
      delete root.dataset.wipe;
    }, 700);

    try {
      const transition = document.startViewTransition(() => apply(next));
      transition.finished.finally(() => {
        window.clearTimeout(settle);
        delete root.dataset.wipe;
      });
    } catch {
      window.clearTimeout(settle);
      delete root.dataset.wipe;
      apply(next);
    }
  });
}
